import React, { useState, useEffect } from "react";
import { useFormContext, FormProvider, Controller } from "react-hook-form";
import { Button, Spinner } from "reactstrap";
import { Collapse, Upload, message } from "antd";
import "react-quill/dist/quill.snow.css";
import { UploadFile } from "antd/es/upload/interface";
import FormTextArea from "../../form-components/TextAreaComponent";
import { AllowedFileTypes } from "../../../fileAccess";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import tenderApis from "../../../api/tender/tenderApis";
import sitesApis from "../../../api/master/sites";
import type { CreateTenderRequest, UpdateTenderRequest } from "../../../api/types/tender";

const { Panel } = Collapse;
const { Dragger } = Upload;
const Others: React.FC<{ prev: () => void; next: () => void }> = (
  props: any
) => {
  const methods = useFormContext();
  const navigate = useNavigate();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { setValue, watch } = methods;
  let formData: any = {
    attachments: [],
  };
  const previousFormDate = watch();
  const [fileList, setFileList] = useState<UploadFile[]>(
    formData.attachments || []
  );
  const [siteSegmentLevel, setSiteSegmentLevel] = useState<string | null>(null);
  const formSiteId = Number(previousFormDate.siteId || previousFormDate.projectId) || 0;

  useEffect(() => {
    if (!formSiteId) {
      setSiteSegmentLevel(null);
      return;
    }
    (async () => {
      try {
        const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(formSiteId);
        if (status && data) {
          setSiteSegmentLevel(typeof data === "string" ? data : data.segmentLevel || data.segment || null);
        } else {
          setSiteSegmentLevel(null);
        }
      } catch {
        setSiteSegmentLevel(null);
      }
    })();
  }, [formSiteId]);

  const attachmentsWatch = watch("attachments");
  useEffect(() => {
    console.log("attachmentsWatch", attachmentsWatch);
    if (attachmentsWatch) {
      setFileList(attachmentsWatch);
    }
  }, [attachmentsWatch]);

  useEffect(() => {
    console.log("previousFormDate", previousFormDate);
  }, []);

  const submitForm = (data: any) => {
    props.next();
  };

  const beforeUpload = (file: UploadFile) => {
    const isAllowedType = Object.values(AllowedFileTypes)
      .filter((value) => typeof value === "string")
      .includes(file.type as AllowedFileTypes);
    if (!isAllowedType) {
      message.error("Invalid file type!");
      return Upload.LIST_IGNORE;
    }
    if (file.size && file.size / 1024 / 1024 >= 2) {
      message.error("File must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }
    // Return false to prevent actual upload - files will be sent manually via API
    return false;
  };

  const handleChange = (info: { fileList: UploadFile[] }) => {
    setFileList(info.fileList);
    setValue("attachments", info.fileList);
  };

  const saveDraft = () => {
    setDisableSubmit(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You want to Save as Draft!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, save!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const formData = previousFormDate;

          let tenderFor: "Material" | "Service" | "Work" = "Material";
          if (formData.tenderFor === "Service / Works") {
            tenderFor = "Service";
          }

          const settings = {
            isLineLevelDelivery: formData.deliveryLineLevel === "true",
            approvalRequiredToPublish: formData.approvalToPublish === "true",
            questionerForEligibility: formData.questionerForEligibility === "true",
            approvalRequiredForEvaluation: formData.approvalForEvaluation === "true",
            documentsFromSuppliersOrContractors: formData.documnetsRequired === "true",
          };

          const materials = tenderFor === "Material"
            ? (formData.items || []).map((item: any) => ({
                materialId: Number(item.materialName) || item.materialId || 0,
                materialName: item.materialLabel || "",
                quantity: Number(item.quantity) || 0,
                uomId: Number(item.uomId) || 0,
                uomName: item.uomLabel || "",
                description: item.description || "",
                deliveryAddressId: item.deliveryAddressId,
                needByDate: item.needByDate
                  ? dayjs(item.needByDate).format("YYYY-MM-DDTHH:mm:ss")
                  : "",
                usage: item.materialUsage || "",
                isInstallationRequired: item.installationRequired || false,
                ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
                ...(item.floorNo ? { floorNo: item.floorNo } : {}),
                ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
                ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
              }))
            : [];

          const tasks = tenderFor !== "Material"
            ? (formData.items || []).map((item: any) => ({
                workType: item.workType || "Open",
                workCategoryId: Number(item.workCategoryId) || 0,
                taskTitle: item.taskTitle || "",
                description: item.description || "",
                quantity: Number(item.quantity) || 0,
                uomId: Number(item.uomId) || 0,
                usage: item.taskUsage || "",
                isInstallationRequired: item.installationRequired || false,
                ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
                ...(item.floorNo ? { floorNo: item.floorNo } : {}),
                ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
                ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
              }))
            : [];

          const questionaries = (formData.questionerSections || []).map((section: any) => {
            const sectionWeightage = section.sectionWeightage || section.weightage || section.totalSetionWeight || "0";
            return {
              sectionName: section.name,
              weightage: Number(String(sectionWeightage).replace(/[^0-9.]/g, "")) || 0,
              approvals: section.approvals || [],
              evaluators: (section.evaluators || []).map((id: any) => ({ userId: Number(id) })),
              questions: (section.questions || []).map((q: any) => ({
                question: q.questioner || q.question || "",
                type: q.questionType || q.type || "Short Text",
                options: q.choiceOptions || q.options || [],
                weightage: Number(String(q.questionWeightage || "0").replace(/[^0-9.]/g, "")) || 0,
                description: q.description || "",
              })),
            };
          });

          const files: File[] = [];
          const filesData: any[] = [];

          if (formData.instructions) {
            const blob = new Blob([formData.instructions], { type: "text/html" });
            files.push(new File([blob], "Instructions.html", { type: "text/html" }));
            filesData.push({ order: files.length - 1, module: "TMS", moduleFileType: "Instructions" });
          }
          if (formData.termsAndConditions) {
            const blob = new Blob([formData.termsAndConditions], { type: "text/html" });
            files.push(new File([blob], "TermsAndConditions.html", { type: "text/html" }));
            filesData.push({ order: files.length - 1, module: "TMS", moduleFileType: "TermsAndConditions" });
          }
          if (formData.attachments && formData.attachments.length > 0) {
            formData.attachments.forEach((attachment: any) => {
              const file = attachment.originFileObj || attachment;
              if (file instanceof File) {
                files.push(file);
                filesData.push({ order: files.length - 1, module: "TMS", moduleFileType: "TenderDocument" });
              }
            });
          }

          const isEdit = !!formData._editMode && !!formData._draftTenderId;

          let response: any;

          if (isEdit) {
            // Build delta-update payload
            const updateMaterials = tenderFor === "Material"
              ? (formData.items || []).map((item: any) => ({
                  ...(item.tenderMaterialId ? { tenderMaterialId: Number(item.tenderMaterialId) } : {}),
                  materialId: Number(item.materialName) || item.materialId || 0,
                  quantity: Number(item.quantity) || 0,
                  uomId: Number(item.uomId) || 0,
                  description: item.description || "",
                  deliveryAddressId: item.deliveryAddressId,
                  needByDate: item.needByDate
                    ? dayjs(item.needByDate).format("YYYY-MM-DDTHH:mm:ss")
                    : "",
                  usage: item.materialUsage || "",
                  isInstallationRequired: item.installationRequired || false,
                  ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
                  ...(item.floorNo ? { floorNo: item.floorNo } : {}),
                  ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
                  ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
                }))
              : [];

            const updateTasks = tenderFor !== "Material"
              ? (formData.items || []).map((item: any) => ({
                  ...(item.tenderTaskId ? { tenderTaskId: Number(item.tenderTaskId) } : {}),
                  workType: item.workType || "Open",
                  workCategoryId: Number(item.workCategoryId) || 0,
                  taskTitle: item.taskTitle || "",
                  description: item.description || "",
                  quantity: Number(item.quantity) || 0,
                  uomId: Number(item.uomId) || 0,
                  usage: item.taskUsage || "",
                  isInstallationRequired: item.installationRequired || false,
                  ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
                  ...(item.floorNo ? { floorNo: item.floorNo } : {}),
                  ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
                  ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
                }))
              : [];

            const currentMaterialIds = new Set(
              (formData.items || [])
                .map((item: any) => item.tenderMaterialId)
                .filter(Boolean)
                .map(Number)
            );
            const deletedMaterials = (formData._originalMaterialIds || []).filter(
              (id: any) => !currentMaterialIds.has(Number(id))
            );

            const currentTaskIds = new Set(
              (formData.items || [])
                .map((item: any) => item.tenderTaskId)
                .filter(Boolean)
                .map(Number)
            );
            const deletedTasks = (formData._originalTaskIds || []).filter(
              (id: any) => !currentTaskIds.has(Number(id))
            );

            const updateQuestionaries = (formData.questionerSections || []).map(
              (section: any) => {
                const sectionWeightage =
                  section.sectionWeightage || section.weightage || "0";
                const sectionId = section.tenderSectionId
                  ? Number(section.tenderSectionId)
                  : undefined;
                const originalQuestionIds: number[] = sectionId
                  ? formData._originalQuestionIdsBySection?.[sectionId] || []
                  : [];
                const currentQuestionIds = new Set(
                  (section.questions || [])
                    .map((q: any) => q.tenderQuestionId)
                    .filter(Boolean)
                    .map(Number)
                );
                const deletedQuestions = originalQuestionIds.filter(
                  (id: number) => !currentQuestionIds.has(Number(id))
                );

                const originalEvaluatorIds: number[] = sectionId
                  ? formData._originalEvaluatorIdsBySection?.[sectionId] || []
                  : [];

                return {
                  ...(sectionId ? { tenderSectionId: sectionId } : {}),
                  sectionName: section.name,
                  weightage:
                    Number(String(sectionWeightage).replace(/[^0-9.]/g, "")) || 0,
                  questions: (section.questions || []).map((q: any) => ({
                    ...(q.tenderQuestionId
                      ? { tenderQuestionId: Number(q.tenderQuestionId) }
                      : {}),
                    question: q.questioner || q.question || "",
                    type: q.questionType || q.type || "Short Text",
                    options: q.choiceOptions || q.options || [],
                    weightage:
                      Number(
                        String(q.questionWeightage || "0").replace(/[^0-9.]/g, "")
                      ) || 0,
                    description: q.description || "",
                  })),
                  deletedQuestions,
                  approvals: section.approvals || [],
                  deletedQuestionaryApprovals: [],
                  evaluators: (section.evaluators || []).map((id: any) => ({
                    userId: Number(id),
                  })),
                  deletedQuestionaryEvaluators: originalEvaluatorIds,
                };
              }
            );

            const currentSectionIds = new Set(
              (formData.questionerSections || [])
                .map((s: any) => s.tenderSectionId)
                .filter(Boolean)
                .map(Number)
            );
            const deletedQuestionaries = (formData._originalSectionIds || []).filter(
              (id: any) => !currentSectionIds.has(Number(id))
            );

            const existingDocIds = new Set(
              (formData.attachments || [])
                .map((a: any) => a.docId)
                .filter(Boolean)
                .map(Number)
            );
            const deletedDocIds: number[] = (formData._originalDocumentIds || []).filter(
              (id: any) =>
                !existingDocIds.has(Number(id)) &&
                id !== formData._instructionsDocId &&
                id !== formData._termsDocId
            );
            if (formData.instructions && formData._instructionsDocId) {
              deletedDocIds.push(formData._instructionsDocId);
            }
            if (formData.termsAndConditions && formData._termsDocId) {
              deletedDocIds.push(formData._termsDocId);
            }

            const supplierMap: Record<string, number> = formData._originalSupplierMap || {};
            const currentSupplierIds = (formData.supplierIds || []).map((id: any) =>
              Number(id)
            );
            const updateSuppliers = currentSupplierIds.map((id: number) => {
              const existingId = supplierMap[String(id)];
              return {
                ...(existingId ? { tenderSupplierId: Number(existingId) } : {}),
                supplierId: id,
              };
            });
            const currentSupplierIdSet = new Set(currentSupplierIds);
            const deletedSuppliers = Object.entries(supplierMap)
              .filter(([sId]) => !currentSupplierIdSet.has(Number(sId)))
              .map(([, tsId]) => Number(tsId));

            const contractorMap: Record<string, number> = formData._originalContractorMap || {};
            const currentContractorIds = (formData.contractorIds || []).map((id: any) =>
              Number(id)
            );
            const updateContractors = currentContractorIds.map((id: number) => {
              const existingId = contractorMap[String(id)];
              return {
                ...(existingId ? { tenderContractorId: Number(existingId) } : {}),
                contractorId: id,
              };
            });
            const currentContractorIdSet = new Set(currentContractorIds);
            const deletedContractors = Object.entries(contractorMap)
              .filter(([cId]) => !currentContractorIdSet.has(Number(cId)))
              .map(([, tcId]) => Number(tcId));

            const updateRequest: UpdateTenderRequest = {
              tenderId: Number(formData._draftTenderId),
              isDraft: true,
              tenderTitle: formData.tenderTitle || "",
              tenderType: formData.tenderType || "Open",
              description: formData.description || "",
              tenderFor,
              lastSubmissionDate: formData.lastSubmissionDate
                ? dayjs(formData.lastSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              siteId: Number(formData.siteId || formData.projectId) || undefined,
              lastDateForExpressingInterest: formData.lastDateForExpressingInterest
                ? dayjs(formData.lastDateForExpressingInterest).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              evaluationStartDate: formData.evaluationStartDate
                ? dayjs(formData.evaluationStartDate).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              sealedBidSubmissionDate: formData.sealedBidSubmissionDate
                ? dayjs(formData.sealedBidSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              sealedBidPostalAddress: formData.sealedBidPostalAddress || undefined,
              settings,
              materials: updateMaterials,
              deletedMaterials,
              tasks: updateTasks,
              deletedTasks,
              questionaries: updateQuestionaries,
              deletedQuestionaries,
              publishApprovals: [],
              deletedPublishApprovals: [],
              evaluationApprovals: [],
              deletedEvaluationApprovals: [],
              suppliers: updateSuppliers,
              deletedSuppliers,
              contractors: updateContractors,
              deletedContractors,
              filesData,
              deletedDocIds,
            };

            response = await tenderApis.updateTender(updateRequest, files);
          } else {
            const request: CreateTenderRequest = {
              tenderNumber: formData.tenderNumber || undefined,
              tenderTitle: formData.tenderTitle || "",
              tenderType: formData.tenderType || "Open",
              description: formData.description || "",
              isDraft: true,
              tenderFor,
              lastSubmissionDate: formData.lastSubmissionDate
                ? dayjs(formData.lastSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
                : "",
              siteId: Number(formData.siteId || formData.projectId) || 0,
              segmentLevel: (siteSegmentLevel as "Tower" | "Villa" | "Floor" | "Flat") || undefined,
              lastDateForExpressingInterest: formData.lastDateForExpressingInterest
                ? dayjs(formData.lastDateForExpressingInterest).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              sealedBidSubmissionDate: formData.sealedBidSubmissionDate
                ? dayjs(formData.sealedBidSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
                : undefined,
              sealedBidPostalAddress: formData.sealedBidPostalAddress || undefined,
              settings,
              materials,
              tasks,
              questionaries,
              publishApprovals: [],
              evaluationApprovals: [],
              filesData,
            };

            response = await tenderApis.createTender(request, files);
          }
          if (response?.status || response?.data) {
            Swal.fire("Saved!", "Your Tender has been Saved as Draft.", "success");
            navigate("/tender/tender-list");
          } else {
            Swal.fire("Error", response?.message || "Failed to save draft", "error");
          }
        } catch (error: any) {
          console.error("Save draft error:", error);
          Swal.fire("Error", error?.message || "Failed to save draft", "error");
        } finally {
          setDisableSubmit(false);
        }
      } else {
        setDisableSubmit(false);
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        className="content clearfix"
        onSubmit={methods.handleSubmit(submitForm)}
      >
        <Collapse defaultActiveKey={["1", "2", "3"]}>
          <Panel header="Instructions (if any)" key="1">
            <FormTextArea
              name="instructions"
              label={undefined}
              placeholder="Enter Instructions"
            />
          </Panel>
          <Panel header="Terms and Conditions (if any)" key="2">
            <FormTextArea
              name="termsAndConditions"
              label={undefined}
              placeholder="Enter Terms and Conditions"
            />
          </Panel>
          <Panel header="Attachments (if any)" key="3">
            <Controller
              name="attachments"
              control={methods.control}
              render={({ field }) => (
                <Dragger
                  beforeUpload={beforeUpload}
                  fileList={fileList}
                  onChange={handleChange}
                  className="ant-upload-drag-custom"
                  action="/purchase-order-new"
                  showUploadList={{ showPreviewIcon: false }}
                  itemRender={(originNode, file, fileList, actions) => {
                    // Customize the rendering of each file to remove the tooltip
                    return React.cloneElement(originNode, {
                      title: null, // Removes the tooltip entirely
                    });
                  }}
                >
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              )}
            />
          </Panel>
        </Collapse>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={props.prev}
                className="btn-dim btn btn-secondary"
              >
                Previous
              </Button>
            </li>
            {/* <li>
              {disableSubmit ? (
                <Button color="secondary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  color="secondary"
                  onClick={() => saveDraft()}
                  className="btn btn-secondary"
                >
                  Save as Draft
                </Button>
              )}
            </li> */}
          </ul>
        </div>
      </form>
    </FormProvider>
  );
};

export default Others;
